import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import { User, LoginRequest, LoginResponse } from '../types';

export class AuthStore {
    user: User | null = null;
    token: string | null = null;
    loading = false;
    error: string | null = null;

    constructor() {
        makeAutoObservable(this);
        this.initializeFromStorage();
    }

    private initializeFromStorage() {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');

        if (token && userStr) {
            try {
                this.token = token;
                this.user = JSON.parse(userStr);
            } catch (error) {
                console.error('Failed to parse stored user data');
                this.clearStorage();
            }
        }
    }

    private clearStorage() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }

    private saveToStorage() {
        if (this.token) {
            localStorage.setItem('token', this.token);
        }
        if (this.user) {
            localStorage.setItem('user', JSON.stringify(this.user));
        }
    }

    async login(credentials: LoginRequest): Promise<boolean> {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.post<LoginResponse>('/api/login', credentials);
            const { token, user } = response.data;

            this.setToken(token);
            this.user = user;
            this.saveToStorage();

            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Неверное имя пользователя или пароль';
            return false;
        } finally {
            this.loading = false;
        }
    }

    logout() {
        this.user = null;
        this.token = null;
        this.clearStorage();
        // Перенаправление будет обработано в компоненте
    }

    setToken(token: string) {
        this.token = token;
    }

    async checkAuth(): Promise<boolean> {
        if (!this.token) {
            return false;
        }

        try {
            // Проверяем токен, делая запрос к защищенному эндпоинту
            await api.get('/api/statistics');

            // Если запрос успешен, токен валиден
            return true;
        } catch (error) {
            // Если получили 401, токен невалиден
            this.logout();
            return false;
        }
    }

    async refreshUserData() {
        if (!this.isAuthenticated) {
            return;
        }

        try {
            // Здесь можно добавить endpoint для получения актуальных данных пользователя
            // const response = await api.get('/api/me');
            // this.user = response.data;
            // this.saveToStorage();
        } catch (error) {
            console.error('Failed to refresh user data');
        }
    }

    get isAuthenticated(): boolean {
        return !!this.token && !!this.user;
    }

    get isAdmin(): boolean {
        return this.user?.role === 'admin';
    }

    get isUser(): boolean {
        return this.user?.role === 'user';
    }

    get username(): string {
        return this.user?.username || '';
    }

    get email(): string {
        return this.user?.email || '';
    }

    get userInitials(): string {
        if (!this.user) return '';
        const names = this.user.username.split(' ');
        if (names.length >= 2) {
            return names[0][0].toUpperCase() + names[1][0].toUpperCase();
        }
        return this.user.username.substring(0, 2).toUpperCase();
    }

    resetError() {
        this.error = null;
    }
}